import React, { useState, useEffect } from 'react';
import {
  X, Save, Loader2, Users, AlertCircle, Plus, Trash2,
  UserPlus, Truck, Clock, Calendar,
} from 'lucide-react';
import toast from 'react-hot-toast';
import supplierService from '../../../../services/supplierService';

const DAY_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ORDER_METHODS = [
  { value: '', label: 'Select Method' },
  { value: 'Phone', label: 'Phone Call' },
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Email', label: 'Email' },
  { value: 'Portal', label: 'Online Portal' },
  { value: 'In-Person', label: 'In-Person' },
];

export default function SupplierForm({ supplier, onClose, onSave }) {
  const isEdit = !!supplier;

  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    altPhone: '',
    email: '',
    gstin: '',
    pan: '',
    fssai: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    category: '',
    paymentTerms: '',
    bankName: '',
    bankAccount: '',
    ifscCode: '',
    notes: '',
    deliveryDays: [],
    leadTimeDays: '',
    minOrderAmount: '',
    orderMethod: '',
    autoApproved: false,
  });

  const [contacts, setContacts] = useState([{ name: '', role: '', phone: '', email: '' }]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name || '',
        contactPerson: supplier.contactPerson || '',
        phone: supplier.phone || '',
        altPhone: supplier.altPhone || '',
        email: supplier.email || '',
        gstin: supplier.gstin || '',
        pan: supplier.pan || '',
        fssai: supplier.fssai || '',
        address: supplier.address || '',
        city: supplier.city || '',
        state: supplier.state || '',
        pincode: supplier.pincode || '',
        category: supplier.category || '',
        paymentTerms: supplier.paymentTerms || '',
        bankName: supplier.bankName || '',
        bankAccount: supplier.bankAccount || '',
        ifscCode: supplier.ifscCode || '',
        notes: supplier.notes || '',
        deliveryDays: supplier.deliveryDays || [],
        leadTimeDays: supplier.leadTimeDays ?? '',
        minOrderAmount: supplier.minOrderAmount ?? '',
        orderMethod: supplier.orderMethod || '',
        autoApproved: supplier.autoApproved ?? false,
      });
      if (supplier.contacts && supplier.contacts.length > 0) {
        setContacts(supplier.contacts.map((c) => ({ ...c })));
      }
    }
  }, [supplier]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const toggleDay = (day) => {
    setFormData((prev) => ({
      ...prev,
      deliveryDays: prev.deliveryDays.includes(day)
        ? prev.deliveryDays.filter((d) => d !== day)
        : [...prev.deliveryDays, day],
    }));
  };

  // Contacts management
  const handleContactChange = (idx, field, value) => {
    setContacts((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const addContact = () => {
    if (contacts.length >= 5) return;
    setContacts((prev) => [...prev, { name: '', role: '', phone: '', email: '' }]);
  };

  const removeContact = (idx) => {
    if (contacts.length <= 1) return;
    setContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Supplier name is required';
    if (!formData.phone.trim()) errs.phone = 'Phone number is required';
    if (formData.phone && !/^\d{10,}$/.test(formData.phone.replace(/[\s-]/g, ''))) errs.phone = 'Enter a valid phone number';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email';
    if (formData.minOrderAmount && isNaN(Number(formData.minOrderAmount))) errs.minOrderAmount = 'Must be a number';
    if (formData.leadTimeDays && isNaN(Number(formData.leadTimeDays))) errs.leadTimeDays = 'Must be a number';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fill all required fields and fix errors');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        leadTimeDays: formData.leadTimeDays !== '' ? Number(formData.leadTimeDays) : null,
        minOrderAmount: formData.minOrderAmount !== '' ? Number(formData.minOrderAmount) : null,
        autoApproved: !!formData.autoApproved,
        contacts: contacts.filter((c) => c.name.trim()),
      };
      if (isEdit) {
        await supplierService.update(supplier.id, payload);
        toast.success('Supplier updated');
      } else {
        await supplierService.create(payload);
        toast.success('Supplier added');
      }
      onSave();
      } catch (err) {
    const message = err?.response?.data?.message || 'Failed to save supplier';
    toast.error(message);
  } finally {
      setSaving(false);
    }
  };

  // renderField is a plain function (not a component) to avoid focus loss on re-render
  const renderField = (label, field, { required, type = 'text', placeholder, colSpan, children } = {}) => (
    <div className={colSpan === 2 ? 'sm:col-span-2' : ''} key={field}>
      <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children || (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 ${
            errors[field] ? 'border-red-300 bg-red-50' : 'border-slate-200'
          }`}
        />
      )}
      {errors[field] && (
        <p className="mt-0.5 text-[10px] text-red-500 flex items-center gap-0.5">
          <AlertCircle size={10} /> {errors[field]}
        </p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-teal-500 flex items-center justify-center">
              <Users size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800">
                {isEdit ? 'Edit Supplier' : 'Add New Supplier'}
              </h2>
              <p className="text-[11px] text-slate-500">
                {isEdit ? 'Update supplier information and delivery preferences' : 'Register a new supplier with full details'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Contact Information */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('Supplier Name', 'name', { required: true, placeholder: 'e.g., Sharma Fresh Vegetables' })}
              {renderField('Contact Person', 'contactPerson', { placeholder: 'e.g., Rajesh Sharma' })}
              {renderField('Phone', 'phone', { required: true, type: 'tel', placeholder: 'e.g., 9876543210' })}
              {renderField('Alternate Phone', 'altPhone', { type: 'tel', placeholder: 'e.g., 011-25643890' })}
              {renderField('Email', 'email', { type: 'email', placeholder: 'e.g., supplier@gmail.com' })}
              {renderField('Category', 'category', { children: (
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                >
                  <option value="">Select Category</option>
                  {supplierService.categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              )})}
            </div>
          </div>

          {/* Multiple Contacts */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> Additional Contacts
              </h3>
              {contacts.length < 5 && (
                <button onClick={addContact} className="flex items-center gap-1 text-[10px] font-medium text-teal-600 hover:text-teal-700">
                  <Plus className="w-3 h-3" /> Add Contact
                </button>
              )}
            </div>
            <div className="space-y-2">
              {contacts.map((contact, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-white rounded-lg border border-slate-200 p-2.5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
                    <input
                      type="text"
                      value={contact.name}
                      onChange={(e) => handleContactChange(idx, 'name', e.target.value)}
                      placeholder="Name"
                      className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    />
                    <input
                      type="text"
                      value={contact.role}
                      onChange={(e) => handleContactChange(idx, 'role', e.target.value)}
                      placeholder="Role (e.g., Sales)"
                      className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    />
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => handleContactChange(idx, 'phone', e.target.value)}
                      placeholder="Phone"
                      className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    />
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(e) => handleContactChange(idx, 'email', e.target.value)}
                      placeholder="Email"
                      className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                    />
                  </div>
                  {contacts.length > 1 && (
                    <button onClick={() => removeContact(idx)} className="p-1 text-slate-400 hover:text-red-500 mt-0.5">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Delivery & Ordering */}
          <div className="bg-blue-50/60 rounded-xl p-4 border border-blue-100">
            <h3 className="text-[10px] uppercase tracking-wide font-semibold text-blue-600 mb-3 flex items-center gap-1">
              <Truck className="w-3 h-3" /> Delivery & Ordering
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Delivery Days */}
              <div className="sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">
                  Delivery Days
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {DAY_OPTIONS.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        formData.deliveryDays.includes(day)
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:bg-teal-50'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {renderField('Lead Time (Days)', 'leadTimeDays', { children: (
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="number"
                    min="0"
                    value={formData.leadTimeDays}
                    onChange={(e) => handleChange('leadTimeDays', e.target.value)}
                    placeholder="e.g., 2"
                    className={`w-full pl-8 pr-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 ${
                      errors.leadTimeDays ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                </div>
              )})}

              {renderField('Minimum Order Amount (₹)', 'minOrderAmount', { children: (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.minOrderAmount}
                    onChange={(e) => handleChange('minOrderAmount', e.target.value)}
                    placeholder="e.g., 5000"
                    className={`w-full pl-7 pr-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400 ${
                      errors.minOrderAmount ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                </div>
              )})}

              {renderField('Preferred Order Method', 'orderMethod', { children: (
                <select
                  value={formData.orderMethod}
                  onChange={(e) => handleChange('orderMethod', e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                >
                  {ORDER_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              )})}

              {renderField('Payment Terms', 'paymentTerms', { children: (
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => handleChange('paymentTerms', e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                >
                  <option value="">Select Terms</option>
                  <option value="COD">Cash on Delivery</option>
                  <option value="Net 7">Net 7 Days</option>
                  <option value="Net 10">Net 10 Days</option>
                  <option value="Net 15">Net 15 Days</option>
                  <option value="Net 30">Net 30 Days</option>
                  <option value="Advance">Advance Payment</option>
                </select>
              )})}

              {/* Auto Approved */}
              <div className="sm:col-span-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.autoApproved}
                    onChange={(e) => handleChange('autoApproved', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-200"
                  />
                  <span>
                    <span className="block text-xs font-semibold text-slate-700">Auto Approved</span>
                    <span className="block text-[10px] text-slate-500">
                      Purchase orders for this supplier are approved automatically without manual review.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-3">Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('Address', 'address', { placeholder: 'Street address', colSpan: 2 })}
              {renderField('City', 'city', { placeholder: 'e.g., New Delhi' })}
              {renderField('State', 'state', { placeholder: 'e.g., Delhi' })}
              {renderField('Pincode', 'pincode', { placeholder: 'e.g., 110033' })}
            </div>
          </div>

          {/* Tax, Compliance & Bank Details */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-3">Tax, Compliance & Bank Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderField('GSTIN', 'gstin', { placeholder: 'e.g., 07AABCS1234F1Z5' })}
              {renderField('PAN', 'pan', { placeholder: 'e.g., AABCS1234F' })}
              {renderField('FSSAI License No.', 'fssai', { placeholder: 'e.g., 10025011000123 (14 digits)' })}
              <div /> {/* spacer */}
              {renderField('Bank Name', 'bankName', { placeholder: 'e.g., State Bank of India' })}
              {renderField('Account Number', 'bankAccount', { placeholder: 'Account number' })}
              {renderField('IFSC Code', 'ifscCode', { placeholder: 'e.g., SBIN0001234' })}
            </div>
          </div>

          {/* Notes */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <h3 className="text-[10px] uppercase tracking-wide font-semibold text-amber-600 mb-2">Notes</h3>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              placeholder="Delivery schedule, special terms, contact preferences..."
              className="w-full px-3 py-2 text-xs border border-amber-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-300/30 focus:border-amber-300 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}